import type { LoginResponse } from '@/stores/auth'
import { apiLogger } from '@/utils/logger'
import { apiClient, handleApiError } from './api'

export interface ShopifyStore {
  id: number
  shop: string
  displayName: string | null
  email: string | null
  currencyCode: string | null
  syncStatus: string
  lastSyncedAt: string | null
  installedAt: string
}

export interface ShopifyConnectionResponse {
  connected: boolean
  store?: ShopifyStore
}

export interface ShopifySyncResponse {
  message: string
  syncStatus: string
}

/**
 * Shopify service that bypasses the standard { success, data } envelope
 * since the Shopify backend endpoints return raw JSON responses.
 */
class ShopifyService {
  private readonly baseURL = '/shopify'

  async getConnectionStatus(): Promise<ShopifyConnectionResponse> {
    try {
      const response = await apiClient.get(`${this.baseURL}/store`)
      return response.data
    } catch (error) {
      apiLogger.error('GET /shopify/store failed', error as Error)
      throw handleApiError(error)
    }
  }

  async disconnectStore(): Promise<void> {
    try {
      await apiClient.delete(`${this.baseURL}/store`)
    } catch (error) {
      apiLogger.error('DELETE /shopify/store failed', error as Error)
      throw handleApiError(error)
    }
  }

  async triggerSync(): Promise<ShopifySyncResponse> {
    try {
      const response = await apiClient.post(`${this.baseURL}/store/sync`)
      return response.data
    } catch (error) {
      apiLogger.error('POST /shopify/store/sync failed', error as Error)
      throw handleApiError(error)
    }
  }

  // Exchanges a one-time token (issued during Shopify-app-store install
  // OAuth callback) for a JWT pair so the merchant lands logged-in without
  // having to enter a password.
  async exchangeSession(token: string): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>(
        `${this.baseURL}/exchange`,
        { token }
      )
      return response.data
    } catch (error) {
      apiLogger.error('POST /shopify/exchange failed', error as Error)
      throw handleApiError(error)
    }
  }

  // Records the wizard URL the merchant should be returned to after the
  // Shopify App Store install completes. Server validates same-origin path.
  async setOnboardingReturn(returnTo: string): Promise<void> {
    try {
      await apiClient.post(`${this.baseURL}/onboarding/intent`, { returnTo })
    } catch (error) {
      apiLogger.error('POST /shopify/onboarding/intent failed', error as Error)
      throw handleApiError(error)
    }
  }
}

export const shopifyService = new ShopifyService()
